import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface GlamourCartItem {
  listingId: string;
  name: string;
  rate: number;                       // formerly price
  compareAtRate?: number;             // for showing savings (formerly compareAtPrice)
  qty: number;

  // Fashion-specific
  size?: string;
  color?: string;
  sku?: string;

  hostId: string;
  brandId: string;
}

interface GlamourServiceFeeState {
  items: GlamourCartItem[];

  subtotal: number;
  totalQty: number;
  totalSavings: number;               // sum of (compareAtRate - rate) * qty

  guestsFee: number;
  hostsFee: number;
  platformProfit: number;
  totalCharged: number;
  deliveryTotal: number;

  calculated: boolean;

  hostProfits: Record<
    string,
    {
      saleTotal: number;
      deliveryTotal: number;
      hostsFee: number;
      hostProfit: number;
    }
  >;

  brandProfits: Record<
    string,
    {
      saleTotal: number;
      deliveryTotal: number;
      hostsFee: number;
      brandProfit: number;
      itemCount: number;
    }
  >;
}

/* ─────────────────────────────────────────────
   UTILITY FUNCTIONS
───────────────────────────────────────────── */

export const calculateGlamourHostEarnings = (total: number) => {
  const hostFeePercentage = 0.1;
  const hostsFee  = parseFloat((total * hostFeePercentage).toFixed(2));
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

/**
 * Tiered guest service fee.
 */
export const calculateGlamourGuestFee = (subtotal: number): number => {
  let guestsFee: number;

  if (subtotal > 5000)       guestsFee = subtotal * 0.2;
  else if (subtotal > 1000)  guestsFee = subtotal * 0.2;
  else if (subtotal > 500)   guestsFee = subtotal * 0.12;
  else if (subtotal > 100)   guestsFee = subtotal * 0.1;
  else                       guestsFee = subtotal * 0.08;

  return parseFloat(guestsFee.toFixed(2));
};

export const calculateGlamourPlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number => parseFloat((hostsFee + guestsFee).toFixed(2));

/**
 * Total savings from sale / compare-at rates across cart.
 */
export const calculateTotalSavings = (items: GlamourCartItem[]): number => {
  const savings = items.reduce((sum, item) => {
    if (!item.compareAtRate || item.compareAtRate <= item.rate) return sum;
    return sum + (item.compareAtRate - item.rate) * item.qty;
  }, 0);
  return parseFloat(savings.toFixed(2));
};

/* ─────────────────────────────────────────────
   ASYNC CALCULATION THUNK
───────────────────────────────────────────── */

export const calculateGlamourEarnings = createAsyncThunk(
  "glamourServiceFees/calculateGlamourEarnings",
  async ({
    items,
    deliveryByBrand,
  }: {
    items: GlamourCartItem[];
    deliveryByBrand: Record<string, number>;
  }) => {
    /* ── Group by host ── */
    const groupedByHost: Record<string, GlamourCartItem[]> = {};
    items.forEach((item) => {
      if (!groupedByHost[item.hostId]) groupedByHost[item.hostId] = [];
      groupedByHost[item.hostId].push(item);
    });

    /* ── Group by brand ── */
    const groupedByBrand: Record<string, GlamourCartItem[]> = {};
    items.forEach((item) => {
      if (!groupedByBrand[item.brandId]) groupedByBrand[item.brandId] = [];
      groupedByBrand[item.brandId].push(item);
    });

    /* ── Per-host earnings ── */
    const hostProfits: GlamourServiceFeeState["hostProfits"] = {};

    let subtotal       = 0;
    let totalQty       = 0;
    let globalHostsFee = 0;
    let globalDelivery = 0;

    for (const hostId in groupedByHost) {
      const hostItems = groupedByHost[hostId];

      const saleTotal = hostItems.reduce((s, i) => s + i.qty * i.rate, 0);

      const hostDelivery = hostItems.reduce(
        (sum, item) => sum + (deliveryByBrand[item.brandId] ?? 0),
        0
      );

      const { hostsFee } = calculateGlamourHostEarnings(saleTotal);

      const hostProfit = parseFloat(
        (saleTotal - hostsFee + hostDelivery).toFixed(2)
      );

      hostProfits[hostId] = {
        saleTotal,
        deliveryTotal: hostDelivery,
        hostsFee,
        hostProfit,
      };

      subtotal       += saleTotal;
      totalQty       += hostItems.reduce((s, i) => s + i.qty, 0);
      globalHostsFee += hostsFee;
      globalDelivery += hostDelivery;
    }

    /* ── Per-brand earnings ── */
    const brandProfits: GlamourServiceFeeState["brandProfits"] = {};

    for (const brandId in groupedByBrand) {
      const brandItems = groupedByBrand[brandId];

      const saleTotal = parseFloat(
        brandItems.reduce((s, i) => s + i.qty * i.rate, 0).toFixed(2)
      );

      const deliveryTotal = deliveryByBrand[brandId] ?? 0;
      const { hostsFee }  = calculateGlamourHostEarnings(saleTotal);
      const brandProfit   = parseFloat((saleTotal - hostsFee + deliveryTotal).toFixed(2));
      const itemCount     = brandItems.reduce((s, i) => s + i.qty, 0);

      brandProfits[brandId] = {
        saleTotal,
        deliveryTotal,
        hostsFee,
        brandProfit,
        itemCount,
      };
    }

    /* ── Global totals ── */
    const guestsFee      = calculateGlamourGuestFee(subtotal);
    const platformProfit = calculateGlamourPlatformProfit(globalHostsFee, guestsFee);
    const totalCharged   = parseFloat((subtotal + guestsFee + globalDelivery).toFixed(2));
    const totalSavings   = calculateTotalSavings(items);

    return {
      items,
      totalQty,
      subtotal,
      totalSavings,
      deliveryTotal: globalDelivery,
      guestsFee,
      hostsFee: globalHostsFee,
      platformProfit,
      totalCharged,
      hostProfits,
      brandProfits,
    };
  }
);

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const initialState: GlamourServiceFeeState = {
  items:          [],
  subtotal:       0,
  totalQty:       0,
  totalSavings:   0,
  guestsFee:      0,
  hostsFee:       0,
  platformProfit: 0,
  totalCharged:   0,
  deliveryTotal:  0,
  calculated:     false,
  hostProfits:    {},
  brandProfits:   {},
};

const glamourServiceFeeSlice = createSlice({
  name: "glamourServiceFees",
  initialState,
  reducers: {
    resetGlamourServiceFees: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      calculateGlamourEarnings.fulfilled,
      (state, action) => {
        state.items          = action.payload.items;
        state.totalQty       = action.payload.totalQty;
        state.subtotal       = action.payload.subtotal;
        state.totalSavings   = action.payload.totalSavings;
        state.deliveryTotal  = action.payload.deliveryTotal;
        state.guestsFee      = action.payload.guestsFee;
        state.hostsFee       = action.payload.hostsFee;
        state.platformProfit = action.payload.platformProfit;
        state.totalCharged   = action.payload.totalCharged;
        state.hostProfits    = action.payload.hostProfits;
        state.brandProfits   = action.payload.brandProfits;
        state.calculated     = true;
      }
    );
  },
});

export const { resetGlamourServiceFees } = glamourServiceFeeSlice.actions;

export default glamourServiceFeeSlice.reducer;